import { useEffect, useMemo, useState } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSites } from '@/hooks/useSites';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import { useMealDeliveries, type MealType } from '@/hooks/useMealDeliveries';
import { useClients } from '@/hooks/useClients';
import { Coffee, Utensils, Sunset, Droplet, Save, UtensilsCrossed, Trash2, ChevronDown } from 'lucide-react';

const ITEMS: { key: MealType; label: string; icon: any; menuPlaceholder: string; menuLabel: string }[] = [
  { key: 'breakfast', label: 'Breakfast', icon: Coffee, menuPlaceholder: 'e.g. Pancakes, Eggs, Fruit', menuLabel: 'Menu' },
  { key: 'lunch', label: 'Lunch', icon: Utensils, menuPlaceholder: 'e.g. Sandwiches, Soup, Salad', menuLabel: 'Menu' },
  { key: 'dinner', label: 'Dinner', icon: Sunset, menuPlaceholder: 'e.g. Pasta, Chicken, Veggies', menuLabel: 'Menu' },
  { key: 'milk', label: 'Milk', icon: Droplet, menuPlaceholder: 'Brand / type (e.g. 2% Whole, Almond)', menuLabel: 'Brand' },
  { key: 'juice', label: 'Juice', icon: Droplet, menuPlaceholder: 'Brand / flavor (e.g. Apple, Orange)', menuLabel: 'Brand' },
];

interface Props {
  defaultSiteId?: string | null;
  date?: Date;
}

export function MealsPanel({ defaultSiteId, date = new Date() }: Props) {
  const { sites = [] } = useSites() as any;
  const [siteId, setSiteId] = useState<string | undefined>(
    defaultSiteId ?? (sites[0]?.id as string | undefined)
  );
  const effectiveSiteId = siteId ?? (sites[0]?.id as string | undefined);
  const dateStr = format(date, 'yyyy-MM-dd');

  const { deliveries, upsertDelivery } = useMealDeliveries(effectiveSiteId, {
    start: startOfDay(date),
    end: endOfDay(date),
  });
  const { serviceLogs } = useServiceLogs();
  const { clients = [] } = useClients() as any;

  const siteClientIds = useMemo(
    () => new Set((clients as any[]).filter((c) => c.site_id === effectiveSiteId).map((c) => c.id)),
    [clients, effectiveSiteId]
  );

  const distributedByItem = useMemo(() => {
    const byItem: Record<MealType, number> = { breakfast: 0, lunch: 0, dinner: 0, milk: 0, juice: 0 };
    const dayStart = startOfDay(date).getTime();
    const dayEnd = endOfDay(date).getTime();
    (serviceLogs as any[]).forEach((log) => {
      const t = (log.service_type || '').toLowerCase();
      if (!['breakfast', 'lunch', 'dinner', 'milk', 'juice'].includes(t)) return;
      const ts = log.created_at ? new Date(log.created_at).getTime() : 0;
      if (ts < dayStart || ts > dayEnd) return;
      if (effectiveSiteId && !siteClientIds.has(log.client_id)) return;
      byItem[t as MealType] += log.quantity || 1;
    });
    return byItem;
  }, [serviceLogs, date, effectiveSiteId, siteClientIds]);

  const [drafts, setDrafts] = useState<Record<MealType, { qty: string; menu: string }>>({
    breakfast: { qty: '', menu: '' },
    lunch: { qty: '', menu: '' },
    dinner: { qty: '', menu: '' },
    milk: { qty: '', menu: '' },
    juice: { qty: '', menu: '' },
  });

  const getDelivery = (m: MealType) =>
    deliveries.find((d) => d.meal_type === m && d.service_date === dateStr);

  const draftFor = (m: MealType) => {
    const local = drafts[m];
    const remote = getDelivery(m);
    return {
      qty: local.qty !== '' ? local.qty : remote ? String(remote.quantity_received) : '',
      menu: local.menu !== '' ? local.menu : remote?.menu_description ?? '',
    };
  };

  const handleSave = (m: MealType) => {
    if (!effectiveSiteId) return;
    const { qty, menu } = draftFor(m);
    const items = menu.split(',').map((s) => s.trim()).filter(Boolean);
    upsertDelivery({
      site_id: effectiveSiteId,
      service_date: dateStr,
      meal_type: m,
      quantity_received: parseInt(qty || '0', 10) || 0,
      menu_description: menu || null,
      menu_items: items,
    });
  };

  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('meals-intake-open') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('meals-intake-open', open ? '1' : '0'); } catch {}
  }, [open]);

  const loggedCount = useMemo(
    () => ITEMS.reduce((acc, { key }) => {
      const r = getDelivery(key);
      return acc + ((r && r.quantity_received > 0) ? 1 : 0);
    }, 0),
    [deliveries, dateStr]
  );

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap py-3 cursor-pointer hover:bg-muted/40 rounded-t-lg transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              />
              <div className="min-w-0">
                <CardTitle className="text-base">Today's Intake — {format(date, 'EEE, MMM d')}</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {open
                    ? "Log meals & beverages received and today's menu. Discarded auto-computes."
                    : `${ITEMS.length} items • ${loggedCount} logged today`}
                </p>
              </div>
            </div>
            {sites.length > 1 && (
              <div onClick={(e) => e.stopPropagation()}>
                <Select value={effectiveSiteId} onValueChange={setSiteId}>
                  <SelectTrigger className="w-[180px] h-8">
                    <SelectValue placeholder="Select facility" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2 pt-0">
            {ITEMS.map(({ key, label, icon: Icon, menuPlaceholder }) => {
              const d = draftFor(key);
              const received = parseInt(d.qty || '0', 10) || 0;
              const distributed = distributedByItem[key];
              const discarded = Math.max(0, received - distributed);
              return (
                <div
                  key={key}
                  className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border border-border rounded-md"
                >
                  <div className="col-span-3 sm:col-span-2 flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm truncate">{label}</span>
                  </div>
                  <Input
                    className="col-span-3 sm:col-span-2 h-8"
                    type="number"
                    min="0"
                    value={d.qty}
                    onChange={(e) => setDrafts((p) => ({ ...p, [key]: { ...p[key], qty: e.target.value } }))}
                    placeholder="Received"
                  />
                  <Input
                    className="col-span-6 sm:col-span-4 h-8"
                    value={d.menu}
                    onChange={(e) => setDrafts((p) => ({ ...p, [key]: { ...p[key], menu: e.target.value } }))}
                    placeholder={menuPlaceholder}
                  />
                  <div className="col-span-9 sm:col-span-3 flex flex-wrap gap-1 justify-end">
                    <Badge variant="secondary" className="text-xs gap-1"><UtensilsCrossed className="h-3 w-3" />Served {distributed}</Badge>
                    <Badge variant="outline" className={`text-xs gap-1 ${discarded > 0 ? 'bg-[hsl(var(--discard)/0.25)] text-[hsl(var(--discard))] border-transparent' : ''}`}>
                      <Trash2 className="h-3 w-3" />Discarded {discarded}
                    </Badge>
                    <Badge variant="outline" className="text-xs">Waste {received > 0 ? Math.round((discarded / received) * 100) : 0}%</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="col-span-3 sm:col-span-1 h-8"
                    onClick={() => handleSave(key)}
                    disabled={!effectiveSiteId}
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
